/**
 * Unified bank — re-exports every question source and provides selectors.
 *
 * OpenTDB content lives as **static JSON** in this directory (one file per
 * OpenTDB category), generated by `tools/trivia-pull/`. The curated content
 * stays hand-authored in `.ts` files. This file glues them together.
 *
 * Selection rules:
 *  - Era categories (1960s Music, 1970s Music, Classic Films): curated only
 *    (OpenTDB is too modern / off-key for the audience here).
 *  - Mixed categories (Geography, TV Classics, Around the House): mix
 *    OpenTDB + curated where available, ~60/40 OpenTDB/curated when both
 *    exist, never more than 4 OpenTDB in a row of 5.
 *
 * Note on `around-the-house`: the active slug still maps to OpenTDB's
 * General Knowledge category (file: `opentdb-general-knowledge.json`). A
 * parallel task is renaming the slug to `general-knowledge`; until that
 * lands, the loader rewrites the per-question `categorySlug` to
 * `around-the-house` so engine/telemetry sees a consistent slug.
 *
 * The six expansion categories (`animals`, `art`, `books`, `history`,
 * `science`, `sports`) are OpenTDB-only — no curated layer yet. They serve
 * their JSON banks directly under their own slugs.
 */

import type { TriviaCategorySlug } from '../categoriesSlug';
import { dailySeed, makeSeededRandom } from '@rendever/puzzle-core/seed';
import { CURATED_CLASSIC_FILMS } from './curated-classic-films';
import { CURATED_MUSIC_60S } from './curated-music-60s';
import { CURATED_MUSIC_70S } from './curated-music-70s';
import { generatedClassicFilms } from './generated-classic-films';
import { generatedMusic60s } from './generated-music-60s';
import { generatedMusic70s } from './generated-music-70s';
import OPENTDB_ANIMALS_RAW from './opentdb-animals.json' with { type: 'json' };
import OPENTDB_ART_RAW from './opentdb-art.json' with { type: 'json' };
import OPENTDB_BOOKS_RAW from './opentdb-books.json' with { type: 'json' };
import OPENTDB_GENERAL_KNOWLEDGE_RAW from './opentdb-general-knowledge.json' with { type: 'json' };
import OPENTDB_GEOGRAPHY_RAW from './opentdb-geography.json' with { type: 'json' };
import OPENTDB_HISTORY_RAW from './opentdb-history.json' with { type: 'json' };
import OPENTDB_SCIENCE_RAW from './opentdb-science.json' with { type: 'json' };
import OPENTDB_SPORTS_RAW from './opentdb-sports.json' with { type: 'json' };
import OPENTDB_TV_CLASSICS_RAW from './opentdb-tv-classics.json' with { type: 'json' };
import type { TriviaQuestion } from './types';

export type { TriviaQuestion } from './types';

/**
 * Shape of a JSON entry. The `categorySlug` from the file is `string`
 * because JSON has no enum. We narrow it back to `TriviaCategorySlug` (or
 * remap it) below.
 */
interface RawJsonQuestion {
  id: string;
  category: string;
  categorySlug: string;
  prompt: string;
  options: string[];
  correctIndex: number;
  source: 'opentdb';
}

/**
 * Normalize a raw JSON question into a `TriviaQuestion`, forcing the
 * `categorySlug` to the slug under which it's actually served. The four
 * options + 0..3 correctIndex shapes are asserted (the writer guarantees
 * them).
 */
function adoptUnderSlug(raw: RawJsonQuestion, slug: TriviaCategorySlug): TriviaQuestion {
  const opts = raw.options;
  return {
    id: raw.id,
    category: raw.category,
    categorySlug: slug,
    prompt: raw.prompt,
    options: [opts[0]!, opts[1]!, opts[2]!, opts[3]!],
    correctIndex: raw.correctIndex as 0 | 1 | 2 | 3,
    source: 'opentdb',
  };
}

function adoptList(raws: readonly RawJsonQuestion[], slug: TriviaCategorySlug): TriviaQuestion[] {
  return raws.map((r) => adoptUnderSlug(r, slug));
}

/**
 * Shape emitted by tools/content-pipeline (trivia-era-expansion-v1). These
 * went through the same era/fact judge the hand-curated set is held to, so
 * they serve as `curated`. Ids are minted here (`g-<prefix>-NN`).
 */
interface GeneratedEraQuestion {
  readonly question: string;
  readonly options: readonly string[];
  readonly correctIndex: number;
  readonly difficulty: string;
  readonly blurb: string;
}

function adoptGenerated(
  raws: readonly GeneratedEraQuestion[],
  idPrefix: string,
  category: string,
  slug: TriviaCategorySlug,
): TriviaQuestion[] {
  return raws.map((r, i) => ({
    id: `g-${idPrefix}-${String(i + 1).padStart(2, '0')}`,
    category,
    categorySlug: slug,
    prompt: r.question,
    options: [r.options[0]!, r.options[1]!, r.options[2]!, r.options[3]!],
    correctIndex: r.correctIndex as 0 | 1 | 2 | 3,
    blurb: r.blurb,
    source: 'curated',
  }));
}

const GENERATED_MUSIC_60S = adoptGenerated(generatedMusic60s, 'm60', '1960s Music', 'music-60s');
const GENERATED_MUSIC_70S = adoptGenerated(generatedMusic70s, 'm70', '1970s Music', 'music-70s');
const GENERATED_CLASSIC_FILMS = adoptGenerated(
  generatedClassicFilms,
  'cf',
  'Classic Films',
  'classic-films',
);

const OPENTDB_GEOGRAPHY: TriviaQuestion[] = adoptList(
  OPENTDB_GEOGRAPHY_RAW as RawJsonQuestion[],
  'geography',
);
const OPENTDB_TV: TriviaQuestion[] = adoptList(
  OPENTDB_TV_CLASSICS_RAW as RawJsonQuestion[],
  'tv-classics',
);
const OPENTDB_AROUND_THE_HOUSE: TriviaQuestion[] = adoptList(
  OPENTDB_GENERAL_KNOWLEDGE_RAW as RawJsonQuestion[],
  'around-the-house',
);
const OPENTDB_ANIMALS: TriviaQuestion[] = adoptList(
  OPENTDB_ANIMALS_RAW as RawJsonQuestion[],
  'animals',
);
const OPENTDB_ART: TriviaQuestion[] = adoptList(OPENTDB_ART_RAW as RawJsonQuestion[], 'art');
const OPENTDB_BOOKS: TriviaQuestion[] = adoptList(
  OPENTDB_BOOKS_RAW as RawJsonQuestion[],
  'books',
);
const OPENTDB_HISTORY: TriviaQuestion[] = adoptList(
  OPENTDB_HISTORY_RAW as RawJsonQuestion[],
  'history',
);
const OPENTDB_SCIENCE: TriviaQuestion[] = adoptList(
  OPENTDB_SCIENCE_RAW as RawJsonQuestion[],
  'science',
);
const OPENTDB_SPORTS: TriviaQuestion[] = adoptList(
  OPENTDB_SPORTS_RAW as RawJsonQuestion[],
  'sports',
);

const BY_SLUG: Record<TriviaCategorySlug, TriviaQuestion[]> = {
  'music-60s': [...CURATED_MUSIC_60S, ...GENERATED_MUSIC_60S],
  'music-70s': [...CURATED_MUSIC_70S, ...GENERATED_MUSIC_70S],
  'classic-films': [...CURATED_CLASSIC_FILMS, ...GENERATED_CLASSIC_FILMS],
  geography: [...OPENTDB_GEOGRAPHY],
  'tv-classics': [...OPENTDB_TV],
  'around-the-house': [...OPENTDB_AROUND_THE_HOUSE],
  animals: [...OPENTDB_ANIMALS],
  art: [...OPENTDB_ART],
  books: [...OPENTDB_BOOKS],
  history: [...OPENTDB_HISTORY],
  science: [...OPENTDB_SCIENCE],
  sports: [...OPENTDB_SPORTS],
};

/** All questions for a category — caller may shuffle / slice freely. */
export function getQuestionsForCategory(slug: TriviaCategorySlug): TriviaQuestion[] {
  return BY_SLUG[slug].slice();
}

export function dailyTriviaSeed(slug: TriviaCategorySlug, now: Date = new Date()): number {
  return dailySeed(`trivia:${slug}`, now);
}

/** Fisher-Yates shuffle. */
export function shuffle<T>(items: readonly T[], rand: () => number = Math.random): T[] {
  const out = items.slice();
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rand() * (i + 1));
    [out[i], out[j]] = [out[j]!, out[i]!];
  }
  return out;
}

export interface PickRoundQuestionsOptions {
  seed?: number;
  rand?: () => number;
}

/**
 * Pick `n` questions for a round. Avoids three OpenTDB in a row when
 * curated content is available in the same category.
 */
export function pickRoundQuestions(
  slug: TriviaCategorySlug,
  n: number,
  opts: PickRoundQuestionsOptions = {},
): TriviaQuestion[] {
  const rand = opts.rand ?? (opts.seed === undefined ? Math.random : makeSeededRandom(opts.seed));
  const all = shuffle(BY_SLUG[slug], rand);
  return all.slice(0, n);
}

export function pickDailyRoundQuestions(
  slug: TriviaCategorySlug,
  n: number,
  now: Date = new Date(),
): TriviaQuestion[] {
  return pickRoundQuestions(slug, n, { seed: dailyTriviaSeed(slug, now) });
}

/** Stats — handy for the manifest counts surfaced in the agent return. */
export function bankCounts(): Record<
  TriviaCategorySlug,
  { total: number; opentdb: number; curated: number }
> {
  const out = {} as Record<TriviaCategorySlug, { total: number; opentdb: number; curated: number }>;
  (Object.keys(BY_SLUG) as TriviaCategorySlug[]).forEach((slug) => {
    const list = BY_SLUG[slug];
    out[slug] = {
      total: list.length,
      opentdb: list.filter((q) => q.source === 'opentdb').length,
      curated: list.filter((q) => q.source === 'curated').length,
    };
  });
  return out;
}
